import { useState, useEffect, useRef } from "react";
import { ChevronDown } from "lucide-react";

/**
 * Phone input with country selector + locked dial-code prefix.
 * The user types only national digits — the dial code is permanent and
 * comes from the selected country (default 🇳🇬 +234).
 *
 * Returns the full E.164 number ("+2348012345678") via onChange.
 */
const COUNTRIES = [
    { code: "NG", flag: "🇳🇬", dial: "+234", name: "Nigeria" },
    { code: "GH", flag: "🇬🇭", dial: "+233", name: "Ghana" },
    { code: "KE", flag: "🇰🇪", dial: "+254", name: "Kenya" },
    { code: "ZA", flag: "🇿🇦", dial: "+27",  name: "South Africa" },
    { code: "UG", flag: "🇺🇬", dial: "+256", name: "Uganda" },
    { code: "TZ", flag: "🇹🇿", dial: "+255", name: "Tanzania" },
    { code: "GB", flag: "🇬🇧", dial: "+44",  name: "United Kingdom" },
    { code: "US", flag: "🇺🇸", dial: "+1",   name: "United States" },
];

const findCountry = (codeOrDial) => {
    if (!codeOrDial) return COUNTRIES[0];
    return (
        COUNTRIES.find((c) => c.code === codeOrDial) ||
        COUNTRIES.find((c) => codeOrDial.startsWith(c.dial)) ||
        COUNTRIES[0]
    );
};

const CountryPhoneField = ({
    value = "",
    onChange,
    placeholder = "8012345678",
    error,
    label = "Phone number",
    testId = "country-phone-field",
    autoFocus = false,
}) => {
    const initialCountry = findCountry(value || "NG");
    const [country, setCountry] = useState(initialCountry);
    const [open, setOpen] = useState(false);
    const [digits, setDigits] = useState(() => {
        if (value && value.startsWith(initialCountry.dial)) {
            return value.slice(initialCountry.dial.length).replace(/\D/g, "");
        }
        return value.replace(/\D/g, "");
    });
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (ev) => {
            if (wrapRef.current && !wrapRef.current.contains(ev.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("touchstart", onDoc);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("touchstart", onDoc);
        };
    }, [open]);

    const emit = (next) => onChange?.(next ? `${country.dial}${next}` : "");

    const handleDigits = (raw) => {
        const cleaned = String(raw || "").replace(/\D/g, "").slice(0, 14);
        setDigits(cleaned);
        emit(cleaned);
    };

    const pickCountry = (c) => {
        setCountry(c);
        setOpen(false);
        // Re-emit with the new dial code so parents stay in sync.
        if (digits) onChange?.(`${c.dial}${digits}`);
    };

    return (
        <div className="block" data-testid={`${testId}-field`} ref={wrapRef}>
            {label && (
                <label className="block text-[12px] font-medium text-muted-foreground mb-1.5 ml-1">
                    {label}
                </label>
            )}
            <div
                className={[
                    "relative flex items-center gap-2 h-14 px-2 rounded-2xl border bg-secondary transition-colors",
                    error
                        ? "border-destructive bg-destructive/5"
                        : "border-border focus-within:bg-background focus-within:border-primary",
                ].join(" ")}
            >
                <button
                    type="button"
                    data-testid={`${testId}-country`}
                    onClick={() => setOpen((o) => !o)}
                    className="h-10 px-3 inline-flex items-center gap-1 rounded-xl bg-background border border-border hover:bg-muted active:scale-[0.97] text-[14px] font-semibold tabular-nums shrink-0"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                >
                    <span className="text-[16px] leading-none">{country.flag}</span>
                    <span>{country.dial}</span>
                    <ChevronDown size={14} className="text-muted-foreground" />
                </button>
                <input
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    value={digits}
                    onChange={(e) => handleDigits(e.target.value)}
                    placeholder={placeholder}
                    autoFocus={autoFocus}
                    data-testid={`${testId}-input`}
                    className="flex-1 bg-transparent outline-none text-[15px] tabular-nums"
                />
                {open && (
                    <ul
                        data-testid={`${testId}-dropdown`}
                        role="listbox"
                        className="absolute left-0 right-0 top-full mt-1.5 max-h-64 overflow-y-auto rounded-2xl border border-border bg-popover shadow-card z-30"
                    >
                        {COUNTRIES.map((c) => (
                            <li key={c.code}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={c.code === country.code}
                                    data-testid={`${testId}-country-${c.code}`}
                                    onClick={() => pickCountry(c)}
                                    className={[
                                        "w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-muted text-[13px]",
                                        c.code === country.code ? "bg-primary/10 text-primary font-medium" : "",
                                    ].join(" ")}
                                >
                                    <span className="text-[16px]">{c.flag}</span>
                                    <span className="flex-1 truncate">{c.name}</span>
                                    <span className="tabular-nums text-muted-foreground">{c.dial}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
            {error && (
                <p className="text-[12px] text-destructive mt-1.5 ml-1" data-testid={`${testId}-error`}>
                    {error}
                </p>
            )}
        </div>
    );
};

export default CountryPhoneField;
export { COUNTRIES };
